import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  MessageCircleMore, 
  DraftingCompass, 
  DollarSign, 
  Scale, 
  BriefcaseBusiness, 
  Languages, 
  HeartPulse, 
  Dumbbell 
} from 'lucide-react';

// Contenedor principal del selector
const SelectorContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(5),
  width: '100%',
}));

// Contenedor de los botones circulares
const ButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  maxWidth: '900px',
  padding: theme.spacing(0, 2),
}));

// Botón circular con borde
const CircleButton = styled(Box)(({ theme, selected, highlighted }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)', // Transición más suave con efecto bounce
    border: `2px solid ${selected || highlighted ? 'transparent' : (isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : (theme.palette.mode === 'dark' ? '#1c1c1e' : '#f2f2f7'))}`,
    backgroundColor: 'transparent',
    position: 'relative',

    '&:hover': {
      transform: 'translateY(-3px)',
      border: `2px solid ${isWallpaperMode ? 'rgba(128, 128, 128, 0.5)' : (theme.palette.mode === 'dark' ? '#2c2c2e' : '#e2e2e7')}`,
    },

    [theme.breakpoints.down('md')]: {
      width: '65px',
      height: '65px',
    },
    [theme.breakpoints.down('sm')]: {
      width: '55px',
      height: '55px',
    },
  };
});

// Contenedor para el texto explicativo
const ExplanationText = styled(Typography)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    textAlign: 'center',
    color: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.85)'
      : (theme.palette.mode === 'dark' ? '#a3a3a3' : '#666666'),
    fontSize: '0.9rem',
    maxWidth: '600px',
    padding: theme.spacing(0, 2),
    marginTop: theme.spacing(2),
    transition: 'opacity 0.3s ease',

    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
    },
  };
});

// Componente para envolver iconos con gradiente
const GradientIcon = ({ children, selected, highlighted, productId }) => {
  const iconId = React.useId();
  
  // Gradientes específicos para cada producto
  const gradients = {
    pro: (
      <linearGradient id={`icon-gradient-${iconId}`} x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#415B7E" />
        <stop offset="50%" stopColor="#2D476A" />
        <stop offset="100%" stopColor="#193356" />
      </linearGradient>
    ),
    edu: (
      <linearGradient id={`icon-gradient-${iconId}`} x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#25505A" />
        <stop offset="100%" stopColor="#113C46" />
      </linearGradient>
    ),
    ups: (
      <linearGradient id={`icon-gradient-${iconId}`} x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#DC9844" />
        <stop offset="50%" stopColor="#C88430" />
        <stop offset="100%" stopColor="#B4701C" />
      </linearGradient>
    ),
    job: (
      <linearGradient id={`icon-gradient-${iconId}`} x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#4A4A4A" />
        <stop offset="50%" stopColor="#333333" />
        <stop offset="100%" stopColor="#1A1A1A" />
      </linearGradient>
    ),
    fit: (
      <linearGradient id={`icon-gradient-${iconId}`} x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#FF8C00" />
        <stop offset="50%" stopColor="#FF6B00" />
        <stop offset="100%" stopColor="#FF5722" />
      </linearGradient>
    ),
    health: (
      <linearGradient id={`icon-gradient-${iconId}`} x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#4CAF50" />
        <stop offset="50%" stopColor="#3d8b40" />
        <stop offset="100%" stopColor="#2e7031" />
      </linearGradient>
    ),
    legal: (
      <linearGradient id={`icon-gradient-${iconId}`} x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#2C5282" />
        <stop offset="50%" stopColor="#234876" />
        <stop offset="100%" stopColor="#1A365D" />
      </linearGradient>
    ),
    translate: (
      <linearGradient id={`icon-gradient-${iconId}`} x1="0%" y1="100%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#3498DB" />
        <stop offset="50%" stopColor="#5D6FBD" />
        <stop offset="100%" stopColor="#9B59B6" />
      </linearGradient>
    )
  };
  
  return (
    <Box sx={{ 
      position: 'relative', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      transition: 'all 0.3s ease-in-out'
    }}>
      <svg width="0" height="0">
        <defs>
          {gradients[productId]}
        </defs>
      </svg>
      {React.cloneElement(children, {
        style: { 
          stroke: (selected || highlighted) ? `url(#icon-gradient-${iconId})` : 'currentColor',
          transition: 'stroke 0.3s ease-in-out',
          opacity: selected ? 1 : highlighted ? 0.9 : 0.7,
          transform: `scale(${(selected || highlighted) ? 1.05 : 1})`,
        }
      })}
    </Box>
  );
};

// Componente para el borde con gradiente
const GradientBorder = styled(Box)(({ theme, productId, highlighted }) => {
  // Mapeo de gradientes por producto
  const gradientMap = {
    pro: 'linear-gradient(to bottom, #415B7E, #2D476A, #193356)',
    edu: 'linear-gradient(to bottom, #25505A, #113C46)',
    ups: 'linear-gradient(to bottom, #DC9844, #C88430, #B4701C)',
    job: 'linear-gradient(to bottom, #4A4A4A, #333333, #1A1A1A)',
    fit: 'linear-gradient(to bottom, #FF8C00, #FF6B00, #FF5722)',
    health: 'linear-gradient(to bottom, #4CAF50, #3d8b40, #2e7031)',
    legal: 'linear-gradient(to bottom, #2C5282, #234876, #1A365D)',
    translate: 'linear-gradient(to right, #3498DB, #5D6FBD, #9B59B6)'
  };

  return {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '50%',
    padding: '2px', // Espacio para el borde
    background: gradientMap[productId],
    zIndex: 0,
    opacity: highlighted ? 0.7 : 1, // Menos opacidad si solo está resaltado
    animation: 'fadeIn 0.4s ease forwards', // Animación de aparición
    '@keyframes fadeIn': {
      '0%': {
        opacity: 0,
      },
      '100%': {
        opacity: highlighted ? 0.7 : 1,
      },
    },
  };
});

// Componente para el fondo del botón
const ButtonBackground = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'absolute',
    top: '2px',
    left: '2px',
    right: '2px',
    bottom: '2px',
    borderRadius: '50%',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark' ? '#000' : '#fff'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    zIndex: 1,
    transition: 'all 0.3s ease-in-out',
  };
});

// Contenedor para centrar el icono correctamente
const IconContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  zIndex: 2,
  width: '100%',
  height: '100%',
  transition: 'transform 0.3s ease-in-out',
});

const TestimonialCategorySelector = ({ selectedProduct, onProductSelect, isMobile }) => {
  // Estado para animar el cambio de texto
  const [fadeText, setFadeText] = useState(false);
  const [displayedProduct, setDisplayedProduct] = useState(selectedProduct);
  
  // Estado para controlar si una transición está en progreso
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Definición de los productos con sus iconos - Memoized to avoid recreation on every render
  const products = React.useMemo(() => [
    { id: 'pro', name: 'AGT Pro', icon: <MessageCircleMore size={isMobile ? 24 : 28} /> },
    { id: 'edu', name: 'AGT Edu', icon: <DraftingCompass size={isMobile ? 24 : 28} /> },
    { id: 'ups', name: 'AGT Ups', icon: <DollarSign size={isMobile ? 24 : 28} /> },
    { id: 'job', name: 'AGT Job', icon: <BriefcaseBusiness size={isMobile ? 24 : 28} /> },
    { id: 'fit', name: 'AGT Fit', icon: <Dumbbell size={isMobile ? 24 : 28} /> },
    { id: 'health', name: 'AGT Health', icon: <HeartPulse size={isMobile ? 24 : 28} /> },
    { id: 'legal', name: 'AGT Legal', icon: <Scale size={isMobile ? 24 : 28} /> },
    { id: 'translate', name: 'AGT Translate', icon: <Languages size={isMobile ? 24 : 28} /> }
  ], [isMobile]); // Only depends on isMobile which affects icon sizes

  // Gestionar la transición del texto explicativo cuando cambia el producto
  useEffect(() => {
    if (displayedProduct !== selectedProduct) {
      setFadeText(true);
      const timer = setTimeout(() => {
        setDisplayedProduct(selectedProduct);
        setFadeText(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [selectedProduct, displayedProduct]);

  // Manejar la selección de producto con transición
  const handleProductSelect = (productId) => {
    if (productId !== selectedProduct) {
      // Si estamos en transición, no permitir cambios
      if (isTransitioning) return;
      
      setIsTransitioning(true);
      
      // Luego cambiar el producto después de un breve retraso
      setTimeout(() => {
        onProductSelect(productId);
        setIsTransitioning(false);
      }, 300);
    }
  };

  return (
    <SelectorContainer>
      <ButtonsContainer>
        {products.map((product) => {
          const isSelected = selectedProduct === product.id;
          
          return (
            <CircleButton
              key={product.id}
              selected={isSelected}
              onClick={() => handleProductSelect(product.id)}
              aria-label={`Filter testimonials for ${product.name}`}
            >
              {isSelected && <GradientBorder productId={product.id} />}
              {isSelected && <ButtonBackground />}
              <IconContainer sx={{ 
                transform: isSelected ? 'scale(1.1)' : 'scale(1)'
              }}>
                <GradientIcon 
                  selected={isSelected}
                  productId={product.id}
                >
                  {product.icon}
                </GradientIcon>
              </IconContainer>
            </CircleButton>
          );
        })}
      </ButtonsContainer>
      
      <ExplanationText sx={{ opacity: fadeText ? 0 : 1 }}>
        {`Showing testimonials for ${products.find(p => p.id === displayedProduct)?.name}. Click on another icon to change product.`}
      </ExplanationText>
    </SelectorContainer>
  );
};

export default TestimonialCategorySelector;